// src/components/common/DataTable/DataTable.jsx
import React, { useState } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CheckSquare,
  Square,
  Loader2
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './DataTable.module.css';

/**
 * DataTable Component
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Column definitions with structure:
 *   [{
 *     key: string,              // Unique identifier for column
 *     header: string,           // Display text for column header
 *     width?: string,           // CSS width value (e.g., '200px', '15%')
 *     sortable?: boolean,       // Enable sorting for this column
 *     render?: function,        // Custom render function (row, value) => ReactNode
 *     className?: string,       // Custom CSS class for cells
 *     headerClassName?: string, // Custom CSS class for header
 *     hidden?: boolean,         // Hide column
 *     align?: 'left' | 'center' | 'right' // Text alignment (default: 'left')
 *   }]
 * @param {Object} props.actions - Actions configuration:
 *   {
 *     render: function,         // Render function (row) => ReactNode
 *     width?: string,           // Width of actions column
 *     frozen?: boolean          // Keep actions column frozen on scroll (default: true)
 *   }
 * @param {boolean} props.selectable - Enable row selection with checkboxes
 * @param {Array} props.selectedRows - Array of selected row IDs (controlled)
 * @param {function} props.onSelectionChange - Callback when selection changes
 * @param {function} props.onRowClick - Callback when row is clicked
 * @param {Object} props.sorting - Sorting state (controlled):
 *   {
 *     orderBy: string,
 *     orderDirection: 'ASC' | 'DESC'
 *   }
 * @param {function} props.onSortChange - Callback when sort changes
 * @param {Object} props.pagination - Pagination configuration:
 *   {
 *     currentPage: number,
 *     pageSize: number,
 *     total: number,
 *     pageSizeOptions?: Array<number>
 *   }
 * @param {function} props.onPaginationChange - Callback for pagination changes
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.emptyMessage - Message to show when no data
 * @param {string} props.emptyDescription - Description in empty state
 * @param {ReactNode} props.emptyIcon - Icon to show in empty state
 * @param {ReactNode} props.emptyAction - Action button/element in empty state
 * @param {string} props.minWidth - Minimum width for table (REMOVED - table auto-sizes now)
 * @param {boolean} props.stickyHeader - Make header sticky on scroll
 * @param {string} props.rowClassName - Function or string for row className
 */
const DataTable = ({
  data = [],
  columns = [],
  actions,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  onRowClick,
  sorting,
  onSortChange,
  pagination,
  onPaginationChange,
  loading = false,
  emptyMessage = 'No data available',
  emptyDescription = '',
  emptyIcon,
  emptyAction,
  minWidth, // Keep for backward compatibility but don't use as default
  stickyHeader = false,
  rowClassName
}) => {
  const { t } = useLocalization();
  const [localSelectedRows, setLocalSelectedRows] = useState([]);

  // Use controlled or uncontrolled selection
  const selectedRowsState = selectedRows || localSelectedRows;
  const setSelectedRowsState = onSelectionChange || setLocalSelectedRows;

  // Handle select all
  const handleSelectAll = () => {
    if (selectedRowsState.length === data.length) {
      setSelectedRowsState([]);
    } else {
      setSelectedRowsState(data.map(row => row.id));
    }
  };

  // Handle individual row selection
  const handleSelectRow = (rowId) => {
    if (selectedRowsState.includes(rowId)) {
      setSelectedRowsState(selectedRowsState.filter(id => id !== rowId));
    } else {
      setSelectedRowsState([...selectedRowsState, rowId]);
    }
  };

  // Handle sort
  const handleSort = (columnKey) => {
    if (!onSortChange) return;

    const newDirection = 
      sorting?.orderBy === columnKey && sorting?.orderDirection === 'ASC' 
        ? 'DESC' 
        : 'ASC';

    onSortChange({
      orderBy: columnKey,
      orderDirection: newDirection
    });
  };

  // Get sort icon for column
  const getSortIcon = (columnKey) => {
    if (sorting?.orderBy !== columnKey) {
      return <ArrowUpDown size={14} className={styles.sortIconInactive} />;
    }
    return sorting.orderDirection === 'ASC' 
      ? <ArrowUp size={14} className={styles.sortIconActive} />
      : <ArrowDown size={14} className={styles.sortIconActive} />;
  };

  // Render cell content
  const renderCell = (column, row) => {
    const value = row[column.key];
    
    if (column.render) {
      return column.render(row, value);
    }
    
    return value ?? '-';
  };

  // Get row class name
  const getRowClassName = (row) => {
    const baseClass = styles.tableRow;
    if (typeof rowClassName === 'function') {
      return `${baseClass} ${rowClassName(row)}`;
    }
    return rowClassName ? `${baseClass} ${rowClassName}` : baseClass;
  };

  // Pagination calculations
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 0;
  const startIndex = pagination ? (pagination.currentPage - 1) * pagination.pageSize + 1 : 0;
  const endIndex = pagination ? Math.min(pagination.currentPage * pagination.pageSize, pagination.total) : 0;

  // Get page numbers to display
  const getPageNumbers = () => {
    if (!pagination) return [];
    
    const pages = [];
    const maxVisiblePages = 5;
    const { currentPage } = pagination;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  // Filter visible columns (non-hidden)
  const visibleColumns = columns.filter(col => !col.hidden);

  // Loading State
  if (loading) {
    return (
      <div className={styles.mainContent}>
        <div className={styles.loadingState}>
          <Loader2 className={styles.spinner} size={48} />
          <p>{t('common.loadingData')}</p>
        </div>
      </div>
    );
  }

  // Empty State
  if (data.length === 0) {
    return (
      <div className={styles.mainContent}>
        <div className={styles.emptyState}>
          {emptyIcon && <div className={styles.emptyIcon}>{emptyIcon}</div>}
          <h3>{emptyMessage}</h3>
          {emptyDescription && <p>{emptyDescription}</p>}
          {emptyAction && <div className={styles.emptyAction}>{emptyAction}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      {/* Table Container with Horizontal Scroll */}
      <div className={styles.tableContainer}>
        <table 
          className={styles.table}
          // Only apply minWidth if explicitly provided, don't use a default
          style={minWidth ? { minWidth } : {}}
        >
          <thead className={stickyHeader ? styles.stickyHeader : ''}>
            <tr>
              {/* Selection Column */}
              {selectable && (
                <th className={styles.checkboxColumn}>
                  <button
                    onClick={handleSelectAll}
                    className={styles.checkboxButton}
                    type="button"
                  >
                    {selectedRowsState.length === data.length ? (
                      <CheckSquare size={18} />
                    ) : (
                      <Square size={18} />
                    )}
                  </button>
                </th>
              )}

              {/* Data Columns */}
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className={`
                    ${column.sortable ? styles.sortableHeader : ''}
                    ${column.headerClassName || ''}
                  `}
                  style={{ 
                    width: column.width,
                    textAlign: column.align || 'left'
                  }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div
                    className={styles.headerContent}
                    style={{ justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start' }}
                  >
                    {column.header}
                    {column.sortable && getSortIcon(column.key)}
                  </div>
                </th>
              ))}

              {/* Actions Column - Frozen */}
              {actions && (
                <th 
                  className={`${styles.actionsColumn} ${actions.frozen !== false ? styles.frozenColumn : ''}`}
                  style={{ width: actions.width || '150px' }}
                >
                  {t('common.actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr
                key={row.id ?? rowIndex}
                className={getRowClassName(row)}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {/* Selection Cell */}
                {selectable && (
                  <td
                    className={styles.checkboxColumn}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleSelectRow(row.id)}
                      className={styles.checkboxButton}
                      type="button"
                    >
                      {selectedRowsState.includes(row.id) ? (
                        <CheckSquare size={18} />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                  </td>
                )}

                {/* Data Cells */}
                {visibleColumns.map((column) => (
                  <td
                    key={column.key}
                    className={column.className || ''}
                    style={{ textAlign: column.align || 'left' }}
                  >
                    {renderCell(column, row)}
                  </td>
                ))}

                {/* Actions Cell - Frozen */}
                {actions && (
                  <td 
                    className={`${styles.actionsCell} ${actions.frozen !== false ? styles.frozenColumn : ''}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {actions.render(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className={styles.pagination}>
          <div className={styles.paginationInfo}>
            Showing {startIndex} to {endIndex} of {pagination.total} entries
          </div>

          <div className={styles.paginationControls}>
            {/* Page Size Selector */}
            {pagination.pageSizeOptions && (
              <div className={styles.pageSizeSelector}>
                <label>{t('common.show')}</label>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => onPaginationChange && onPaginationChange({
                    ...pagination,
                    pageSize: parseInt(e.target.value),
                    currentPage: 1
                  })}
                  className={styles.pageSizeSelect}
                >
                  {pagination.pageSizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Page Navigation */}
            <div className={styles.pageButtons}>
              <button
                className={styles.pageButton}
                onClick={() => onPaginationChange && onPaginationChange({
                  ...pagination,
                  currentPage: 1
                })}
                disabled={pagination.currentPage === 1}
                title={t('common.firstPage')}
              >
                <ChevronsLeft size={18} />
              </button>

              <button
                className={styles.pageButton}
                onClick={() => onPaginationChange && onPaginationChange({
                  ...pagination,
                  currentPage: pagination.currentPage - 1
                })}
                disabled={pagination.currentPage === 1}
                title={t('common.previousPage')}
              >
                <ChevronLeft size={18} />
              </button>

              {getPageNumbers().map(page => (
                <button
                  key={page}
                  className={`${styles.pageButton} ${pagination.currentPage === page ? styles.active : ''}`}
                  onClick={() => onPaginationChange && onPaginationChange({
                    ...pagination,
                    currentPage: page
                  })}
                >
                  {page}
                </button>
              ))}

              <button
                className={styles.pageButton}
                onClick={() => onPaginationChange && onPaginationChange({
                  ...pagination,
                  currentPage: pagination.currentPage + 1
                })}
                disabled={pagination.currentPage === totalPages}
                title={t('common.nextPage')}
              >
                <ChevronRight size={18} />
              </button>

              <button
                className={styles.pageButton}
                onClick={() => onPaginationChange && onPaginationChange({
                  ...pagination,
                  currentPage: totalPages
                })}
                disabled={pagination.currentPage === totalPages}
                title={t('common.lastPage')}
              >
                <ChevronsRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;